import React from "react";
import { useNavigate, useOutletContext } from "react-router-dom";

import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";

import {
  AlertCircle,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Download,
  FileCheck2,
  FileText,
  Gauge,
  Info,
  Leaf,
  LoaderCircle,
  RefreshCw,
  Trash2,
  UploadCloud,
  X,
  ChevronDown,
  Zap,
} from "lucide-react";

import { useToast } from "../../../components/feedback/ToastProvider";

import "./RenovationProjectEnergyPerformanceCertificateStep.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const STEP_DRAFT_KEY = "ENERGY_PERFORMANCE_CERTIFICATE";

const STEP_CODES = [
  "ENERGY_PERFORMANCE_CERTIFICATE",
  "MID_PROJECT_EPC",
  "PROJECT_EPC",
];

/* =========================================================
   OPTIONS
   ========================================================= */

const DATA_SOURCE_OPTIONS = [
  {
    value: "MANUAL_ENTRY",
    labelKey: "options.dataSource.manual",
    fallback: "Manual entry",
  },
  {
    value: "OFFICIAL_EPC",
    labelKey: "options.dataSource.official",
    fallback: "Official EPC document",
  },
  {
    value: "PRE_ASSESSMENT_SIMULATION",
    labelKey: "options.dataSource.preAssessment",
    fallback: "Pre-assessment simulation",
  },
  {
    value: "RENOVATION_PLAN_SIMULATION",
    labelKey: "options.dataSource.renovationPlan",
    fallback: "Renovation-plan simulation",
  },
  {
    value: "OSS_ESTIMATION",
    labelKey: "options.dataSource.ossEstimate",
    fallback: "OSS estimation",
  },
];

const ACCREDITATION_OPTIONS = [
  {
    value: "ARCHITECT",
    labelKey: "options.accreditation.architect",
    fallback: "Architect",
  },
  {
    value: "TECHNICAL_ARCHITECT",
    labelKey: "options.accreditation.technicalArchitect",
    fallback: "Technical Architect",
  },
  {
    value: "ENGINEER",
    labelKey: "options.accreditation.engineer",
    fallback: "Engineer",
  },
  {
    value: "TECHNICAL_ENGINEER",
    labelKey: "options.accreditation.technicalEngineer",
    fallback: "Technical Engineer",
  },
];

const SECTOR_OPTIONS = [
  {
    value: "BUILDING_REHABILITATION",
    labelKey: "options.sector.buildingRehabilitation",
    fallback: "Building rehabilitation",
  },
  {
    value: "RENEWABLE_ENERGIES",
    labelKey: "options.sector.renewableEnergies",
    fallback: "Renewable energies",
  },
];

const RATING_OPTIONS = [
  {
    value: "A_PLUS",
    label: "A+",
  },
  {
    value: "A",
    label: "A",
  },
  {
    value: "B",
    label: "B",
  },
  {
    value: "C",
    label: "C",
  },
  {
    value: "D",
    label: "D",
  },
  {
    value: "E",
    label: "E",
  },
  {
    value: "F",
    label: "F",
  },
  {
    value: "G",
    label: "G",
  },
];

const EMPTY_FORM = {
  data_source: "MANUAL_ENTRY",

  certifier_name: "",
  professional_accreditation: "",
  certificate_date: "",
  sector: "",

  energy_rating: "",
  emission_rating: "",

  primary_non_renewable_energy_consumption: "",

  co2_emissions: "",

  notes: "",
};

/* =========================================================
   HELPERS
   ========================================================= */

function findStep(steps = []) {
  return (
    steps.find((item) =>
      STEP_CODES.includes(String(item?.step_code || "").toUpperCase()),
    ) || {
      step_code: "ENERGY_PERFORMANCE_CERTIFICATE",

      step_order: 4,

      status: "NOT_STARTED",

      is_optional: true,

      visible_to: "HB_OSS",

      editable_by: "HB_OSS",
    }
  );
}

function statusClass(status) {
  return String(status || "NOT_STARTED")
    .toLowerCase()
    .replaceAll("_", "-");
}

function formatFileSize(bytes) {
  const value = Number(bytes);

  if (!Number.isFinite(value) || value <= 0) {
    return "";
  }

  if (value < 1024) {
    return `${value} B`;
  }

  if (value < 1024 * 1024) {
    return `${(value / 1024).toFixed(1)} KB`;
  }

  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTime(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normaliseEpcForm(epc) {
  if (!epc) {
    return {
      ...EMPTY_FORM,
    };
  }

  return {
    data_source: epc.data_source || "MANUAL_ENTRY",

    certifier_name: epc.certifier_name || "",

    professional_accreditation: epc.professional_accreditation || "",

    certificate_date: epc.certificate_date || "",

    sector: epc.sector || "",

    energy_rating: epc.energy_rating || "",

    emission_rating: epc.emission_rating || "",

    primary_non_renewable_energy_consumption:
      epc.primary_non_renewable_energy_consumption ?? "",

    co2_emissions: epc.co2_emissions ?? "",

    notes: epc.notes || "",
  };
}

function validatePdf(file, t) {
  if (!(file instanceof File)) {
    return t("errors.fileRequired", "Please select a PDF file.");
  }

  const filename = String(file.name || "").toLowerCase();

  if (!filename.endsWith(".pdf")) {
    return t("errors.pdfOnly", "Only PDF files are allowed.");
  }

  const contentType = String(file.type || "").toLowerCase();

  if (
    contentType &&
    ![
      "application/pdf",
      "application/x-pdf",
      "application/octet-stream",
    ].includes(contentType)
  ) {
    return t("errors.pdfOnly", "Only PDF files are allowed.");
  }

  return "";
}

function hasMeaningfulData(form, documentFile, documentInfo) {
  return Boolean(
    documentFile ||
    documentInfo ||
    form.certifier_name ||
    form.professional_accreditation ||
    form.certificate_date ||
    form.sector ||
    form.energy_rating ||
    form.emission_rating ||
    form.primary_non_renewable_energy_consumption !== "" ||
    form.co2_emissions !== "" ||
    form.notes ||
    form.data_source !== "MANUAL_ENTRY",
  );
}

/* =========================================================
   COMPONENT
   ========================================================= */

export default function RenovationProjectEnergyPerformanceCertificateStep() {
  const { t } = useTranslation("renovation-project-epc");

  const navigate = useNavigate();

  const { getAccessTokenSilently } = useAuth0();

  const { showToast } = useToast();

  const {
    project,
    steps,
    basePath,
    viewerRole,

    refreshProject,

    registerStepSaveHandler,

    setStepDirty,

    isStepDirty,

    isStepSaving,

    getStepDraft,

    setStepDraft,

    clearStepDraft,
  } = useOutletContext();

  const step = findStep(steps);

  /* =========================================================
     STATE
     ========================================================= */

  const [form, setForm] = React.useState({
    ...EMPTY_FORM,
  });

  const [savedEpc, setSavedEpc] = React.useState(null);

  const [documentInfo, setDocumentInfo] = React.useState(null);

  const [documentFile, setDocumentFile] = React.useState(null);

  const [viewer, setViewer] = React.useState({
    role: viewerRole || "",

    can_edit: false,

    can_upload_document: false,

    can_delete_document: false,
  });

  const [stepStatus, setStepStatus] = React.useState(
    step.status || "NOT_STARTED",
  );

  const [loading, setLoading] = React.useState(true);

  const [loadError, setLoadError] = React.useState("");

  const [fieldErrors, setFieldErrors] = React.useState({});

  const [isLocalSaving, setIsLocalSaving] = React.useState(false);

  const [isDownloading, setIsDownloading] = React.useState(false);

  const [isDeletingDocument, setIsDeletingDocument] = React.useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);

  const fileInputRef = React.useRef(null);

  const localDirtyRef = React.useRef(false);

  /* =========================================================
     DERIVED
     ========================================================= */

  const canEdit = Boolean(viewer.can_edit);

  const canUploadDocument = Boolean(
    viewer.can_upload_document ?? viewer.can_edit,
  );

  const isOfficialEpc = form.data_source === "OFFICIAL_EPC";

  const meaningfulData = hasMeaningfulData(form, documentFile, documentInfo);

  /* =========================================================
     DRAFT
     ========================================================= */

  const persistDraft = React.useCallback(
    (nextForm, nextFile) => {
      if (typeof setStepDraft !== "function") {
        return;
      }

      setStepDraft(STEP_DRAFT_KEY, {
        form: nextForm,
        documentFile: nextFile || null,
      });
    },
    [setStepDraft],
  );

  /* =========================================================
     APPLY RESPONSE
     ========================================================= */

  const applyResponse = React.useCallback(
    (data, { restoreDraft = false } = {}) => {
      const epc = data?.epc || null;

      const nextForm = normaliseEpcForm(epc);

      const nextDocument = epc?.document || data?.document || null;

      setSavedEpc(epc);

      setForm(nextForm);

      setDocumentInfo(nextDocument);

      setViewer({
        role: data?.viewer?.role || viewerRole || "",

        can_edit: Boolean(data?.viewer?.can_edit),

        can_upload_document: Boolean(
          data?.viewer?.can_upload_document ?? data?.viewer?.can_edit,
        ),

        can_delete_document: Boolean(data?.viewer?.can_delete_document),
      });

      setStepStatus(data?.step_status || step.status || "NOT_STARTED");

      if (restoreDraft && typeof getStepDraft === "function") {
        const draft = getStepDraft(STEP_DRAFT_KEY);

        if (draft?.form) {
          setForm({
            ...nextForm,
            ...draft.form,
          });

          if (draft.documentFile instanceof File) {
            setDocumentFile(draft.documentFile);
          }

          localDirtyRef.current = true;

          setStepDirty?.(true);
        }
      }
    },
    [getStepDraft, setStepDirty, step.status, viewerRole],
  );

  /* =========================================================
     LOAD
     ========================================================= */

  const loadEpc = React.useCallback(
    async ({ restoreDraft = true, showLoading = true } = {}) => {
      if (!project?.id) {
        return;
      }

      if (showLoading) {
        setLoading(true);
      }

      setLoadError("");

      try {
        const token = await getAccessTokenSilently();

        const response = await fetch(
          `${API_BASE}/api/projects/${project.id}/epc/project/`,
          {
            method: "GET",

            headers: {
              Authorization: `Bearer ${token}`,

              Accept: "application/json",
            },
          },
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data?.detail ||
              t("errors.loadFailed", "Failed to load the project EPC."),
          );
        }

        applyResponse(data, {
          restoreDraft,
        });
      } catch (requestError) {
        setLoadError(
          requestError?.message ||
            t("errors.loadFailed", "Failed to load the project EPC."),
        );
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    },
    [applyResponse, getAccessTokenSilently, project?.id, t],
  );

  React.useEffect(() => {
    loadEpc({
      restoreDraft: true,

      showLoading: true,
    });
  }, [loadEpc]);

  /* =========================================================
     FORM CHANGE
     ========================================================= */

  const updateField = React.useCallback(
    (field, value) => {
      if (!canEdit) {
        return;
      }

      setForm((current) => {
        const next = {
          ...current,
          [field]: value,
        };

        persistDraft(next, documentFile);

        return next;
      });

      setFieldErrors((current) => ({
        ...current,
        [field]: "",
      }));

      localDirtyRef.current = true;

      setStepDirty?.(true);
    },
    [canEdit, documentFile, persistDraft, setStepDirty],
  );

  /* =========================================================
     FILE
     ========================================================= */

  const selectFile = React.useCallback(
    (file) => {
      if (!canUploadDocument) {
        return;
      }

      const validationError = validatePdf(file, t);

      if (validationError) {
        showToast({
          type: "error",

          title: t("toasts.invalidFileTitle", "Invalid EPC document"),

          message: validationError,
        });

        return;
      }

      setDocumentFile(file);

      persistDraft(form, file);

      localDirtyRef.current = true;

      setStepDirty?.(true);
    },
    [canUploadDocument, form, persistDraft, setStepDirty, showToast, t],
  );

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    if (file) {
      selectFile(file);
    }

    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();

    if (!canUploadDocument) {
      return;
    }

    const file = event.dataTransfer.files?.[0];

    if (file) {
      selectFile(file);
    }
  };

  const removePendingFile = () => {
    setDocumentFile(null);

    persistDraft(form, null);

    localDirtyRef.current = true;

    setStepDirty?.(true);
  };

  /* =========================================================
     VALIDATION
     ========================================================= */

  const validateForm = React.useCallback(() => {
    const errors = {};

    const primaryRaw = form.primary_non_renewable_energy_consumption;

    if (primaryRaw !== "" && primaryRaw !== null && primaryRaw !== undefined) {
      const value = Number(primaryRaw);

      if (!Number.isFinite(value) || value < 0) {
        errors.primary_non_renewable_energy_consumption = t(
          "validation.invalidNumber",
          "Enter a valid non-negative number.",
        );
      }
    }

    const co2Raw = form.co2_emissions;

    if (co2Raw !== "" && co2Raw !== null && co2Raw !== undefined) {
      const value = Number(co2Raw);

      if (!Number.isFinite(value) || value < 0) {
        errors.co2_emissions = t(
          "validation.invalidNumber",
          "Enter a valid non-negative number.",
        );
      }
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      showToast({
        type: "error",

        title: t("toasts.validationTitle", "Check the EPC information"),

        message: t(
          "toasts.validationMessage",
          "Please correct the highlighted values before continuing.",
        ),
      });

      return false;
    }

    return true;
  }, [form, showToast, t]);

  /* =========================================================
     SAVE
     ========================================================= */

  const saveEpc = React.useCallback(
    async ({ notify = true } = {}) => {
      if (!project?.id) {
        throw new Error(
          t("errors.projectUnavailable", "Project is not available."),
        );
      }

      if (!meaningfulData) {
        localDirtyRef.current = false;

        setStepDirty?.(false);

        clearStepDraft?.(STEP_DRAFT_KEY);

        return true;
      }

      if (!validateForm()) {
        return false;
      }

      if (!canEdit) {
        throw new Error(
          t("errors.editNotAllowed", "You cannot edit the project EPC."),
        );
      }

      setIsLocalSaving(true);

      try {
        const token = await getAccessTokenSilently();

        const formData = new FormData();

        formData.append("certificate_stage", "PROJECT");

        formData.append("data_source", form.data_source || "MANUAL_ENTRY");

        formData.append("certifier_name", form.certifier_name || "");

        formData.append(
          "professional_accreditation",
          form.professional_accreditation || "",
        );

        formData.append("certificate_date", form.certificate_date || "");

        formData.append("sector", form.sector || "");

        formData.append("energy_rating", form.energy_rating || "");

        formData.append("emission_rating", form.emission_rating || "");

        formData.append(
          "primary_non_renewable_energy_consumption",
          form.primary_non_renewable_energy_consumption,
        );

        formData.append("co2_emissions", form.co2_emissions);

        formData.append("notes", form.notes || "");

        if (documentFile instanceof File) {
          formData.append("file", documentFile);
        }

        const response = await fetch(
          `${API_BASE}/api/projects/${project.id}/epc/project/`,
          {
            method: "POST",

            headers: {
              Authorization: `Bearer ${token}`,
            },

            body: formData,
          },
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          if (data?.errors && typeof data.errors === "object") {
            const nextErrors = {};

            Object.entries(data.errors).forEach(([key, value]) => {
              nextErrors[key] = Array.isArray(value)
                ? value[0]
                : String(value || "");
            });

            setFieldErrors(nextErrors);
          }

          throw new Error(
            data?.detail ||
              t("errors.saveFailed", "Failed to save the project EPC."),
          );
        }

        applyResponse(data);

        setDocumentFile(null);

        setFieldErrors({});

        localDirtyRef.current = false;

        clearStepDraft?.(STEP_DRAFT_KEY);

        setStepDirty?.(false);

        if (notify) {
          showToast({
            type: "success",

            title: t("toasts.savedTitle", "Energy performance saved"),

            message: t(
              "toasts.savedMessage",
              "The project EPC information was saved successfully.",
            ),
          });
        }

        return true;
      } catch (requestError) {
        const message =
          requestError?.message ||
          t("errors.saveFailed", "Failed to save the project EPC.");

        if (notify) {
          showToast({
            type: "error",

            title: t("toasts.saveErrorTitle", "Could not save EPC information"),

            message,
          });
        }

        throw new Error(message);
      } finally {
        setIsLocalSaving(false);
      }
    },
    [
      applyResponse,
      canEdit,
      clearStepDraft,
      documentFile,
      form,
      getAccessTokenSilently,
      meaningfulData,
      project?.id,
      setStepDirty,
      showToast,
      t,
      validateForm,
    ],
  );

  /* =========================================================
     SHARED PROJECT SAVE HANDLER
     ========================================================= */

  React.useEffect(() => {
    if (!registerStepSaveHandler || !["HB", "OSS"].includes(viewerRole)) {
      return undefined;
    }

    return registerStepSaveHandler(() =>
      saveEpc({
        notify: false,
      }),
    );
  }, [registerStepSaveHandler, saveEpc, viewerRole]);

  /* =========================================================
     DOWNLOAD
     ========================================================= */

  const downloadDocument = async () => {
    if (!project?.id || !documentInfo?.id || isDownloading) {
      return;
    }

    setIsDownloading(true);

    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/projects/${project.id}/documents/${documentInfo.id}/file/?disposition=attachment`,
        {
          method: "GET",

          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));

        throw new Error(
          data?.detail ||
            t("errors.downloadFailed", "Failed to download the EPC document."),
        );
      }

      const blob = await response.blob();

      const objectUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = objectUrl;

      link.download =
        documentInfo.filename ||
        documentInfo.original_filename ||
        "project-epc.pdf";

      document.body.appendChild(link);

      link.click();
      link.remove();

      window.URL.revokeObjectURL(objectUrl);
    } catch (requestError) {
      showToast({
        type: "error",

        title: t("toasts.downloadErrorTitle", "Download failed"),

        message:
          requestError?.message ||
          t("errors.downloadFailed", "Failed to download the EPC document."),
      });
    } finally {
      setIsDownloading(false);
    }
  };

  /* =========================================================
     DELETE DOCUMENT
     ========================================================= */

  const deleteDocument = async () => {
    if (!project?.id || !documentInfo?.id || isDeletingDocument) {
      return;
    }

    setIsDeletingDocument(true);

    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/projects/${project.id}/epc/project/document/`,
        {
          method: "DELETE",

          headers: {
            Authorization: `Bearer ${token}`,

            Accept: "application/json",
          },
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            t("errors.deleteFailed", "Failed to remove the EPC document."),
        );
      }

      setDocumentInfo(null);

      setDeleteModalOpen(false);

      if (data?.epc) {
        setSavedEpc(data.epc);
      }

      if (data?.step_status) {
        setStepStatus(data.step_status);
      }

      showToast({
        type: "success",

        title: t("toasts.documentDeletedTitle", "EPC document removed"),

        message: t(
          "toasts.documentDeletedMessage",
          "The document was removed. The energy-performance data has been kept.",
        ),
      });

      await refreshProject?.();
    } catch (requestError) {
      showToast({
        type: "error",

        title: t("toasts.deleteErrorTitle", "Could not remove document"),

        message:
          requestError?.message ||
          t("errors.deleteFailed", "Failed to remove the EPC document."),
      });
    } finally {
      setIsDeletingDocument(false);
    }
  };

  /* =========================================================
     CONTINUE
     ========================================================= */

  const goNext = async () => {
    if (localDirtyRef.current || isStepDirty) {
      try {
        const saved = await saveEpc({
          notify: true,
        });

        if (!saved) {
          return;
        }

        await refreshProject?.();
      } catch {
        return;
      }
    }

    navigate(`${basePath}/${project.id}/home-renovation`);
  };

  /* =========================================================
     LOADING
     ========================================================= */

  if (loading) {
    return (
      <div className="project-epc-loader">
        <LoaderCircle size={30} className="project-epc-spinner" />

        <span>{t("loading", "Loading energy performance information...")}</span>
      </div>
    );
  }

  /* =========================================================
     ERROR
     ========================================================= */

  if (loadError) {
    return (
      <div className="project-epc-load-error">
        <AlertCircle size={28} />

        <strong>
          {t(
            "errors.loadTitle",
            "Energy performance information could not be loaded",
          )}
        </strong>

        <span>{loadError}</span>

        <button
          type="button"
          onClick={() =>
            loadEpc({
              restoreDraft: true,

              showLoading: true,
            })
          }
        >
          <RefreshCw size={16} />

          {t("buttons.tryAgain", "Try again")}
        </button>
      </div>
    );
  }

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <>
      <article className="project-epc-step">
        {/* ===================================================
            HEADER
            =================================================== */}

        <header className="project-epc-step__header">
          <div className="project-epc-step__heading">
            <span className="project-epc-step__eyebrow">
              <Zap size={15} />

              {t("header.eyebrow", "Step 4")}
            </span>

            <div className="project-epc-step__title-row">
              <h1>{t("header.title", "Project / mid-project EPC")}</h1>

              <span className="project-epc-step__optional">
                {t("header.optional", "Optional")}
              </span>
            </div>

            <p>
              {t(
                "header.subtitle",
                "Record the current energy performance of the property using an official EPC or the latest available project simulation.",
              )}
            </p>
          </div>

          <div className="project-epc-step__status">
            <span className="project-epc-step__stage-count">
              {t("header.stage", "Stage {{current}} of {{total}}", {
                current: step.step_order || 4,

                total: 8,
              })}
            </span>

            <span
              className={
                `project-epc-step__status-pill ` +
                `project-epc-step__status-pill--${statusClass(stepStatus)}`
              }
            >
              {t(
                `stepStatuses.${stepStatus}`,
                String(stepStatus).replaceAll("_", " ").toLowerCase(),
              )}
            </span>
          </div>
        </header>

        {/* ===================================================
            OPTIONAL STEP NOTICE
            =================================================== */}

        <div className="project-epc-info">
          <Info size={19} />

          <div>
            <strong>
              {t(
                "info.title",
                "A mid-project EPC is not required in every renovation",
              )}
            </strong>

            <p>
              {t(
                "info.text",
                "If no official certificate has been issued yet, the latest energy values may come from the pre-assessment, renovation plan or an OSS estimation. You can also leave this step empty and continue.",
              )}
            </p>
          </div>
        </div>

        {/* ===================================================
            SOURCE
            =================================================== */}

        <section className="project-epc-card project-epc-card--source">
          <div className="project-epc-card__heading">
            <div className="project-epc-card__icon">
              <Gauge size={20} />
            </div>

            <div>
              <h2>{t("source.title", "Energy data source")}</h2>

              <p>
                {t(
                  "source.description",
                  "Indicate where the current energy-performance values come from.",
                )}
              </p>
            </div>
          </div>

          <div className="project-epc-form-field project-epc-form-field--wide">
            <label htmlFor="epc-data-source">
              {t("fields.dataSource", "Source of energy-performance data")}
            </label>

            <div className="project-epc-select">
              <select
                id="epc-data-source"
                value={form.data_source}
                disabled={!canEdit}
                onChange={(event) =>
                  updateField("data_source", event.target.value)
                }
              >
                {DATA_SOURCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey, option.fallback)}
                  </option>
                ))}
              </select>

              <ChevronDown
                size={18}
                className="project-epc-select__arrow"
                aria-hidden="true"
              />
            </div>
          </div>

          {!isOfficialEpc ? (
            <div className="project-epc-source-note">
              <Leaf size={17} />

              <span>
                {t(
                  "source.simulationNotice",
                  "For simulated or estimated values, an external certifier and EPC document are not required.",
                )}
              </span>
            </div>
          ) : null}
        </section>

        {/* ===================================================
            CERTIFICATE DOCUMENT
            =================================================== */}

        <section className="project-epc-card">
          <div className="project-epc-card__heading">
            <div className="project-epc-card__icon">
              <FileText size={20} />
            </div>

            <div>
              <h2>
                {t("document.title", "Energy Performance Certificate document")}
              </h2>

              <p>
                {t(
                  "document.description",
                  "Upload the official EPC when one is available. The document is optional for this stage.",
                )}
              </p>
            </div>

            <span className="project-epc-card__optional">
              {t("common.optional", "Optional")}
            </span>
          </div>

          {documentInfo ? (
            <div className="project-epc-document">
              <div className="project-epc-document__icon">
                <FileCheck2 size={23} />
              </div>

              <div className="project-epc-document__content">
                <strong>
                  {documentInfo.filename ||
                    documentInfo.original_filename ||
                    t(
                      "document.fileFallback",
                      "Energy Performance Certificate",
                    )}
                </strong>

                <div className="project-epc-document__meta">
                  {documentInfo.created_at ? (
                    <span>{formatDateTime(documentInfo.created_at)}</span>
                  ) : null}

                  {formatFileSize(documentInfo.size_bytes) ? (
                    <span>{formatFileSize(documentInfo.size_bytes)}</span>
                  ) : null}
                </div>
              </div>

              <div className="project-epc-document__actions">
                <button
                  type="button"
                  className="project-epc-action-button"
                  onClick={downloadDocument}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <LoaderCircle size={16} className="project-epc-spinner" />
                  ) : (
                    <Download size={16} />
                  )}

                  {t("buttons.download", "Download")}
                </button>

                {viewer.can_delete_document ? (
                  <button
                    type="button"
                    className="project-epc-action-button project-epc-action-button--danger"
                    onClick={() => setDeleteModalOpen(true)}
                  >
                    <Trash2 size={16} />

                    {t("buttons.delete", "Delete")}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {canUploadDocument ? (
            <div className="project-epc-upload">
              <span className="project-epc-upload__label">
                {documentInfo
                  ? t("document.replaceLabel", "Replace EPC document")
                  : t(
                      "document.uploadLabel",
                      "Energy Performance Certificate document",
                    )}
              </span>

              {documentFile ? (
                <div className="project-epc-pending-file">
                  <div className="project-epc-pending-file__icon">
                    <UploadCloud size={21} />
                  </div>

                  <div className="project-epc-pending-file__content">
                    <span>
                      {documentInfo
                        ? t(
                            "document.replacementSelected",
                            "Replacement selected",
                          )
                        : t("document.fileSelected", "File selected")}
                    </span>

                    <strong>{documentFile.name}</strong>

                    <small>{formatFileSize(documentFile.size)}</small>
                  </div>

                  <button
                    type="button"
                    className="project-epc-pending-file__remove"
                    onClick={removePendingFile}
                    aria-label={t("buttons.removeFile", "Remove selected file")}
                  >
                    <X size={17} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="project-epc-dropzone"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={handleDrop}
                >
                  <UploadCloud size={22} />

                  <span>
                    <strong>
                      {t("document.drop", "Drag & drop a PDF here")}
                    </strong>{" "}
                    {t("document.or", "or")}{" "}
                    <u>{t("document.browse", "browse")}</u>
                  </span>
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept=".pdf,application/pdf"
                onChange={handleFileChange}
              />

              <p className="project-epc-upload__hint">
                {t(
                  "document.hint",
                  "PDF only. The document is optional because mid-project energy values may also come from a simulation.",
                )}
              </p>
            </div>
          ) : null}
        </section>

        {/* ===================================================
            CERTIFICATE DETAILS
            =================================================== */}

        <section className="project-epc-card">
          <div className="project-epc-card__heading">
            <div className="project-epc-card__icon">
              <CheckCircle2 size={20} />
            </div>

            <div>
              <h2>{t("certificate.title", "Certificate details")}</h2>

              <p>
                {t(
                  "certificate.description",
                  "Professional and administrative information associated with the certificate, when available.",
                )}
              </p>
            </div>
          </div>

          <div className="project-epc-form-grid">
            <div className="project-epc-form-field">
              <label htmlFor="epc-certifier">
                {t("fields.certifier", "Certifier")}

                <span>{t("common.optional", "Optional")}</span>
              </label>

              <input
                id="epc-certifier"
                type="text"
                value={form.certifier_name}
                disabled={!canEdit}
                placeholder={t("placeholders.certifier", "Name and surname")}
                onChange={(event) =>
                  updateField("certifier_name", event.target.value)
                }
              />
            </div>

            <div className="project-epc-form-field">
              <label htmlFor="epc-accreditation">
                {t("fields.accreditation", "Professional accreditation")}

                <span>{t("common.optional", "Optional")}</span>
              </label>

              <div className="project-epc-select">
                <select
                  id="epc-accreditation"
                  value={form.professional_accreditation}
                  disabled={!canEdit}
                  onChange={(event) =>
                    updateField(
                      "professional_accreditation",
                      event.target.value,
                    )
                  }
                >
                  <option value="">
                    {t("placeholders.accreditation", "Select accreditation")}
                  </option>

                  {ACCREDITATION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.labelKey, option.fallback)}
                    </option>
                  ))}
                </select>

                <ChevronDown
                  size={18}
                  className="project-epc-select__arrow"
                  aria-hidden="true"
                />
              </div>
            </div>

            <div className="project-epc-form-field">
              <label htmlFor="epc-date">
                {t("fields.certificateDate", "Date of certificate")}

                <span>{t("common.optional", "Optional")}</span>
              </label>

              <div className="project-epc-date-field">
                <input
                  id="epc-date"
                  type="date"
                  value={form.certificate_date}
                  disabled={!canEdit}
                  onChange={(event) =>
                    updateField("certificate_date", event.target.value)
                  }
                />

                <CalendarDays size={17} />
              </div>
            </div>

            <div className="project-epc-form-field">
              <label htmlFor="epc-sector">
                {t("fields.sector", "Sector")}

                <span>{t("common.optional", "Optional")}</span>
              </label>

              <div className="project-epc-select">
                <select
                  id="epc-sector"
                  value={form.sector}
                  disabled={!canEdit}
                  onChange={(event) =>
                    updateField("sector", event.target.value)
                  }
                >
                  <option value="">
                    {t("placeholders.sector", "Select sector")}
                  </option>

                  {SECTOR_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.labelKey, option.fallback)}
                    </option>
                  ))}
                </select>

                <ChevronDown
                  size={18}
                  className="project-epc-select__arrow"
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ===================================================
            ENERGY PERFORMANCE
            =================================================== */}

        <section className="project-epc-card project-epc-card--performance">
          <div className="project-epc-card__heading">
            <div className="project-epc-card__icon">
              <Leaf size={20} />
            </div>

            <div>
              <h2>{t("performance.title", "Energy performance")}</h2>

              <p>
                {t(
                  "performance.description",
                  "These indicators are required when a project EPC or simulation is recorded.",
                )}
              </p>
            </div>
          </div>

          <div className="project-epc-form-grid">
            <div
              className={`project-epc-form-field ${
                fieldErrors.energy_rating ? "has-error" : ""
              }`}
            >
              <label htmlFor="epc-energy-rating">
                {t("fields.energyRating", "Energy rating")}
              </label>

              <div className="project-epc-select">
                <select
                  id="epc-energy-rating"
                  value={form.energy_rating}
                  disabled={!canEdit}
                  onChange={(event) =>
                    updateField("energy_rating", event.target.value)
                  }
                >
                  <option value="">
                    {t("placeholders.rating", "Select rating")}
                  </option>

                  {RATING_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <ChevronDown
                  size={18}
                  className="project-epc-select__arrow"
                  aria-hidden="true"
                />
              </div>

              {fieldErrors.energy_rating ? (
                <span className="project-epc-field-error">
                  {fieldErrors.energy_rating}
                </span>
              ) : null}
            </div>

            <div
              className={`project-epc-form-field ${
                fieldErrors.primary_non_renewable_energy_consumption
                  ? "has-error"
                  : ""
              }`}
            >
              <label htmlFor="epc-primary-energy">
                {t(
                  "fields.primaryEnergy",
                  "Primary non-renewable energy consumption",
                )}
              </label>

              <div className="project-epc-number-field">
                <input
                  id="epc-primary-energy"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.primary_non_renewable_energy_consumption}
                  disabled={!canEdit}
                  placeholder="0.00"
                  onChange={(event) =>
                    updateField(
                      "primary_non_renewable_energy_consumption",
                      event.target.value,
                    )
                  }
                />

                <span>kWh/m²·year</span>
              </div>

              {fieldErrors.primary_non_renewable_energy_consumption ? (
                <span className="project-epc-field-error">
                  {fieldErrors.primary_non_renewable_energy_consumption}
                </span>
              ) : null}
            </div>

            <div
              className={`project-epc-form-field ${
                fieldErrors.emission_rating ? "has-error" : ""
              }`}
            >
              <label htmlFor="epc-emission-rating">
                {t("fields.emissionRating", "Emission rating")}
              </label>

              <div className="project-epc-select">
                <select
                  id="epc-emission-rating"
                  value={form.emission_rating}
                  disabled={!canEdit}
                  onChange={(event) =>
                    updateField("emission_rating", event.target.value)
                  }
                >
                  <option value="">
                    {t("placeholders.rating", "Select rating")}
                  </option>

                  {RATING_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <ChevronDown
                  size={18}
                  className="project-epc-select__arrow"
                  aria-hidden="true"
                />
              </div>

              {fieldErrors.emission_rating ? (
                <span className="project-epc-field-error">
                  {fieldErrors.emission_rating}
                </span>
              ) : null}
            </div>

            <div
              className={`project-epc-form-field ${
                fieldErrors.co2_emissions ? "has-error" : ""
              }`}
            >
              <label htmlFor="epc-co2">
                {t("fields.co2", "CO₂ emissions")}
              </label>

              <div className="project-epc-number-field">
                <input
                  id="epc-co2"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.co2_emissions}
                  disabled={!canEdit}
                  placeholder="0.00"
                  onChange={(event) =>
                    updateField("co2_emissions", event.target.value)
                  }
                />

                <span>kgCO₂/m²·year</span>
              </div>

              {fieldErrors.co2_emissions ? (
                <span className="project-epc-field-error">
                  {fieldErrors.co2_emissions}
                </span>
              ) : null}
            </div>
          </div>
        </section>

        {/* ===================================================
            NOTES
            =================================================== */}

        <section className="project-epc-card project-epc-card--notes">
          <div className="project-epc-form-field project-epc-form-field--wide">
            <label htmlFor="epc-notes">
              {t("fields.notes", "Notes")}

              <span>{t("common.optional", "Optional")}</span>
            </label>

            <textarea
              id="epc-notes"
              rows={4}
              value={form.notes}
              disabled={!canEdit}
              placeholder={t(
                "placeholders.notes",
                "Add any relevant information about the EPC or simulated values...",
              )}
              onChange={(event) => updateField("notes", event.target.value)}
            />
          </div>
        </section>

        {/* ===================================================
            CONTINUE
            =================================================== */}

        <button
          type="button"
          className="project-epc-next"
          onClick={goNext}
          disabled={isLocalSaving || isStepSaving || isDeletingDocument}
        >
          {isLocalSaving || isStepSaving ? (
            <LoaderCircle size={18} className="project-epc-spinner" />
          ) : null}

          <span>
            {localDirtyRef.current || isStepDirty
              ? t("buttons.saveContinue", "Save and continue to Works budget")
              : t("buttons.continue", "Continue to Works budget")}
          </span>

          <ArrowRight size={18} />
        </button>
      </article>

      {/* =====================================================
          DELETE DOCUMENT MODAL
          ===================================================== */}

      {deleteModalOpen ? (
        <div
          className="project-epc-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isDeletingDocument) {
              setDeleteModalOpen(false);
            }
          }}
        >
          <div
            className="project-epc-delete-modal"
            role="dialog"
            aria-modal="true"
          >
            <div className="project-epc-delete-modal__icon">
              <Trash2 size={23} />
            </div>

            <h2>{t("deleteModal.title", "Remove EPC document?")}</h2>

            <p>
              {t(
                "deleteModal.message",
                "Only the uploaded document will be removed. The EPC energy-performance values will remain available.",
              )}
            </p>

            {documentInfo?.filename ? (
              <div className="project-epc-delete-modal__file">
                <FileText size={16} />

                <span>{documentInfo.filename}</span>
              </div>
            ) : null}

            <div className="project-epc-delete-modal__actions">
              <button
                type="button"
                className="project-epc-modal-button"
                disabled={isDeletingDocument}
                onClick={() => setDeleteModalOpen(false)}
              >
                {t("buttons.cancel", "Cancel")}
              </button>

              <button
                type="button"
                className="project-epc-modal-button project-epc-modal-button--danger"
                disabled={isDeletingDocument}
                onClick={deleteDocument}
              >
                {isDeletingDocument ? (
                  <LoaderCircle size={16} className="project-epc-spinner" />
                ) : (
                  <Trash2 size={16} />
                )}

                {t("buttons.removeDocument", "Remove document")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
